import { defineRule } from 'vee-validate';

defineRule("alphanumeric", (value, _params, ctx) => {
    if(!value) {return true;}
    let count = (value.match(/^[0-9A-Za-z]+$/));
    return !!count || `The ${fieldName(ctx)} field must include only alphanumeric characters.`;
});

// Vee-validate 4 dereferences `@fieldName` references to the field's value
// before invoking the rule (and ctx.rule.params holds the resolved values, not
// the original tokens). To still surface the source field name in the error,
// reverse-lookup ctx.form for the value we were given, then translate the
// field's vid into its human-readable label via the registry below.
const fieldLabels = new Map();
function targetName(target, ctx, fallback) {
    if (target == null || !ctx?.form) return fallback;
    const vid = Object.keys(ctx.form).find(k => ctx.form[k] === target);
    if (!vid) return fallback;
    return fieldLabels.get(vid) || vid;
}

// vee-validate exposes the field's resolved label (or its name when no label is set)
// as ctx.field — the same source the global generateMessage() in main.js uses. Inline
// rule messages interpolate it so they read "The {label} field ..." instead of a
// generic "The field ...". Falls back to "field" when the context is unavailable.
function fieldName(ctx) {
    return ctx?.field || 'field';
}

defineRule('dateGreater', (value, [target], ctx) => {
    if (!value || !target || new Date(value) > new Date(target)) return true;
    return `The ${fieldName(ctx)} field should be after ${targetName(target, ctx, 'the target date')}`;
});

defineRule('dateGreaterOrEqual', (value, [target], ctx) => {
    if (dateGreaterOrEqualTest(value, target)) return true;
    return `The ${fieldName(ctx)} field should be the same as or after ${targetName(target, ctx, 'the target date')}`;
});

export function dateGreaterOrEqualTest(value, target) {
  return !value || !target || new Date(value) >= new Date(target);
}

defineRule('dateLessThanOrEqual', (value, [target], ctx) => {
    if (!value || !target || new Date(value) <= new Date(target)) return true;
    return `The ${fieldName(ctx)} field should be the same as or before ${targetName(target, ctx, 'the target date')}`;
});

defineRule('dateLessThan', (value, [target], ctx) => {
    if (!value || !target || new Date(value) < new Date(target)) return true;
    return `The ${fieldName(ctx)} field should be before ${targetName(target, ctx, 'the target date')}`;
});

defineRule('qtyGreater', (value, [target], ctx) => {
    if (!target || value > target) return true;
    return `The ${fieldName(ctx)} field should be greater than ${targetName(target, ctx, 'the target')}`;
});

defineRule('isbnValid', (value, { isValid }, ctx) => {
    return (value && isValid) || `The ${fieldName(ctx)} field format is invalid`;
});

defineRule('zeroThroughNineOrX', (value, _params, ctx) => {
    var numbersOrX = new RegExp(
        /^([0-9-]{10,}X|[0-9-]{10,}|978[0-9-]{10,}|979[0-9-]{10,})$/
    );
    return (value && numbersOrX.test(value)) || `The ${fieldName(ctx)} field must contain only digits 0-9 or X`;
});

defineRule('wholesaleCost', (value, args, ctx) => {
    const { buyersGuideCost, isBuyersGuide } = Array.isArray(args)
        ? { buyersGuideCost: args[0], isBuyersGuide: args[1] === 'true' || args[1] === true }
        : args;
    let cost = parseFloat(buyersGuideCost);
    if(isBuyersGuide && value > cost)
    {
        return `The ${fieldName(ctx)} field must be equal to or below $` + buyersGuideCost;
    }
    return true;
});

defineRule("p-integer", (value, _params, ctx) => {
    if (value === null || value === undefined || value === '') return true;
    value = parseFloat(value);
    if(value > 2147483647){
        return `The ${fieldName(ctx)} field exceeds the maximum allowed.`;
    }
    if(value < -2147483648){
        return `The ${fieldName(ctx)} field exceeds the minimum allowed.`;
    }
    let valid = !isNaN(value)
            && isFinite(value)
            && Number.isInteger(value)
            && value <= 2147483647
            && value >= -2147483648;
    return valid || `The ${fieldName(ctx)} field must be an integer.`;
});

defineRule('required', (value) => {
    // Return false (not a literal message) so the global generateMessage in main.js
    // supplies the label-aware "The {field} field is required" text.
    if (['', null, undefined].indexOf(value) !== -1) {
        return false;
    }
    return true;
});

// For p-select / p-advanced-select bound to int FK ids — the backend often
// serializes unset ids as 0 (default int), which slips past the regular
// `required` rule. selectRequired also rejects 0 and uses a "Please select"
// message generated in main.js's generateMessage config.
defineRule('selectRequired', (value) => {
    if (value === null || value === undefined || value === '' || value === 0) {
        return false;
    }
    return true;
});

defineRule('oneOf', (value, args, ctx) => {
    return args.includes(String(value)) || `The ${fieldName(ctx)} field must be one of: ${args.join(', ')}`;
});

defineRule("p-decimal", (value, params, ctx) => {
    if (value === null || value === undefined || value === '') return true;
    const args = Array.isArray(params) ? { precision: Number(params[0]), scale: Number(params[1]) } : params;
    if (!pDecimalValidationTest(value, args)) {
        let absValue = Math.abs(value) + '';
        let leftHand = absValue;
        let rightHand = '';
        if(absValue.indexOf('.') > -1){
            var splitValue = absValue.split('.');
            leftHand = splitValue[0];
            rightHand = splitValue[1];
        }
        if(leftHand.length > (args.precision - args.scale)){
            return `The ${fieldName(ctx)} field allows maximum of ` + (args.precision - args.scale)  + ' digits to the left of the decimal.';
        }
        if(rightHand.length > args.scale){
            return `The ${fieldName(ctx)} field allows maximum of ` + args.scale  + ' digits to the right of the decimal.';
        }
        return `The ${fieldName(ctx)} field must be a decimal.`;
    }
    return true;
});

export function pDecimalValidationTest(value, params) {
  const args = Array.isArray(params) ? { precision: Number(params[0]), scale: Number(params[1]) } : params;
  let regex = new RegExp('^\\d{0,' + (args.precision - args.scale) + '}(\\.\\d{0,' + args.scale + '})?$');
  return !isNaN(parseFloat(value))
          && isFinite(value)
          && regex.test(Math.abs(value));
}

defineRule("decimal", (value, _params, ctx) => {
    return (!isNaN(parseFloat(value)) && isFinite(value)) || `The ${fieldName(ctx)} field must be a decimal.`;
});

defineRule("exceedQty", (value, args, ctx) => {
    const { totalQty, compareQty } = Array.isArray(args)
        ? { totalQty: args[0], compareQty: args[1] }
        : args;
    return (parseInt(compareQty) <= parseInt(totalQty)) || `The ${fieldName(ctx)} field can't exceed the qty of ` + totalQty;
});

defineRule("exceedInvoicedQty", (value, args, ctx) => {
    const { totalInvoicedQty, totalReturnQty } = Array.isArray(args)
        ? { totalInvoicedQty: args[0], totalReturnQty: args[1] }
        : args;
    let sum = parseInt(value) + parseInt(totalReturnQty);
    return (sum <= parseInt(totalInvoicedQty)) || `The ${fieldName(ctx)} field can't exceed the invoiced qty of ` + totalInvoicedQty;
});

defineRule("exceedReturnedQty", (value, args) => {
    // Support both object-arg and string-syntax (positional) forms.
    const { returnQty, summedMemoQty } = Array.isArray(args)
        ? { returnQty: args[0], summedMemoQty: args[1] }
        : args;
    return (parseInt(summedMemoQty) <= parseInt(returnQty)) || 'Resolved + Cancelled + Denied Qty\'s can\'t exceed the Returned Qty of ' + returnQty;
});

defineRule("exceedOrderQty", (value, {orderQty, summedQty}) => {
    return (parseInt(summedQty) <= parseInt(orderQty)) || 'Shipped + Cancelled + Back Ordered Qty\'s can\'t exceed the Order Qty of ' + orderQty;
});

defineRule("exceedMemoAmount", (value, args) => {
    //todo: use the currency formatter to format the memo amount.
    //may need to change the rules to an object instead of a string and pass in the formatter
    //similar to some of the import configurations.
    const { memoAmount, summedResolvedAmount } = Array.isArray(args)
        ? { memoAmount: args[0], summedResolvedAmount: args[1] }
        : args;
    return (parseInt(summedResolvedAmount) <= parseInt(memoAmount)) || `Total resolved amount cannot exceed memo amount of $${memoAmount}`;
});

defineRule("isUnique", (value, args) => {
    let isUniqueValue, message;
    if (Array.isArray(args)) {
        // string-syntax form: "isUnique:true,Some message"
        // (string args avoid creating a new object literal on every render,
        // which would otherwise loop the parent Form.)
        isUniqueValue = args[0] === true || args[0] === 'true';
        message = args[1];
    } else {
        ({ isUniqueValue, message } = args);
    }
    // An unset uniqueness result (e.g. a serial/rental row not yet validated by its
    // async @change check) is treated as valid, mirroring is-valid-serial-number.
    // Otherwise a freshly-typed value shows the "already in use" message before the
    // check has run. The string-syntax form always yields a strict boolean, so this
    // only affects the object form where the value can be undefined/null.
    if (isUniqueValue == undefined) return true;
    return isUniqueValue || message || 'The value must be unique';
});

import uniqueid from 'lodash.uniqueid'
export default {
  // ValidationProvider is registered globally in main.js as VeeField/ValidationProvider
  props: {
    rules: {
      default: ''
    },
    vid: {
      type: String,
      default () { return uniqueid('input_'); } //vid must be unique on a page for validation to work properly
    },
    mode: {
      type: String,
      default: undefined
    }
  },
  created() {
    // Prefer the human-readable label, but fall back to `name` for fields that set a
    // name instead of a label (e.g. table-cell inputs like the discount tier Low/High).
    // Without this, cross-field rules (qtyGreater, dateGreater, ...) surface the raw vid.
    const display = this.label || this.name;
    if (this.vid && display) {
      fieldLabels.set(this.vid, display);
    }
  },
  beforeUnmount() {
    if (this.vid) {
      fieldLabels.delete(this.vid);
    }
  },
  watch: {
    label(newLabel) {
      const display = newLabel || this.name;
      if (this.vid && display) {
        fieldLabels.set(this.vid, display);
      }
    }
  },
  computed: {
    isRequired() {
      // Lowercase so `selectRequired` (and any future *Required variant) also
      // shows the asterisk, since includes() is case-sensitive.
      return this.rules && (typeof this.rules === "string" && this.rules.toLowerCase().includes("required")) || (typeof this.rules === "object" && this.rules.isRequired);
    },
    // Select components bind their ValidationProvider to this instead of `rules`.
    // An unselected int-FK id serializes as 0, which the global `required` rule
    // treats as valid — so on a select, swap a bare `required` for the 0-rejecting
    // `selectRequired`. Callers can then just write rules="required" everywhere.
    // Number/text inputs keep using `rules` directly, since 0 is a legitimate
    // value there. Existing explicit `selectRequired` usages are left untouched.
    selectRules() {
      const rules = this.rules;
      if (!rules) return rules;
      if (typeof rules === "string") {
        return rules
          .split("|")
          .map(r => (r.trim() === "required" ? "selectRequired" : r))
          .join("|");
      }
      if (typeof rules === "object" && "required" in rules) {
        const { required, ...rest } = rules;
        return { ...rest, selectRequired: required };
      }
      return rules;
    },
    labelText() {
        if (this.label) {
            return this.label + (this.isRequired ? " *" : "");
        }
        return null;
    }
  }
};
